import { getISOWeek, startOfISOWeek, addDays, format } from "date-fns";
import type { Absence } from "@/hooks/use-absences";
import { absenceDisplayHours } from "./absence-display";
import { countWorkingDaysInOverlap } from "./billing-calculations";
import { parseISODate } from "./format";

/**
 * Ren logikk for ansattkortet (/ansatte/:id, VIB-3028). Fanene viser det
 * disse funksjonene returnerer, så ukegruppering, fraværssummer og
 * prosjektbøtter kan testes uten å rendre noe.
 */

export interface WeekGroup<T> {
  /** Mandag i uka, "YYYY-MM-DD". */
  weekStart: string;
  weekNumber: number;
  /** Mandag og søndag i uka, for «26.–1. nov»-etiketten. */
  start: Date;
  end: Date;
  totalHours: number;
  entries: T[];
}

/**
 * Grupperer timeføringer per ISO-uke (mandag til søndag), nyeste uke først og
 * nyeste føring først i hver uke. Tidligere ble mandag regnet som
 * `getDate() - getDay() + 1`, som flyttet søndager til uka etter.
 */
export function groupEntriesByIsoWeek<T extends { date: string; duration_seconds: number }>(
  entries: T[],
): WeekGroup<T>[] {
  const groups = new Map<string, WeekGroup<T>>();
  for (const entry of entries) {
    const date = new Date(entry.date);
    const start = startOfISOWeek(date);
    const key = format(start, "yyyy-MM-dd");
    let group = groups.get(key);
    if (!group) {
      group = {
        weekStart: key,
        weekNumber: getISOWeek(date),
        start,
        end: addDays(start, 6),
        totalHours: 0,
        entries: [],
      };
      groups.set(key, group);
    }
    group.entries.push(entry);
    group.totalHours += (entry.duration_seconds ?? 0) / 3600;
  }
  return [...groups.values()]
    .sort((a, b) => b.weekStart.localeCompare(a.weekStart))
    .map((g) => ({
      ...g,
      entries: [...g.entries].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
    }));
}

export interface AbsenceRow {
  absence: Absence;
  /** Ukedager i perioden. Null for deldagsfravær (`hours` satt). */
  workingDays: number | null;
  hours: number;
}

export interface AbsenceSummary {
  rows: AbsenceRow[];
  approvedHours: number;
  pendingHours: number;
}

/**
 * Fravær som starter i `year`. Godkjent og ventende summeres hver for seg, og
 * avslått telles ikke. Varigheten er arbeidsdager (man–fre), samme regel som
 * kapasitetstrekket og sykefraværskortet bruker.
 */
export function summarizeAbsences(absences: Absence[], year: number): AbsenceSummary {
  const rows = absences
    .filter((a) => (a.start_date ?? "").startsWith(String(year)))
    .map((absence) => {
      const start = parseISODate(absence.start_date);
      const end = parseISODate(absence.end_date);
      return {
        absence,
        workingDays: absence.hours != null ? null : countWorkingDaysInOverlap(start, end, start, end),
        hours: absenceDisplayHours(absence),
      };
    });
  let approvedHours = 0;
  let pendingHours = 0;
  for (const row of rows) {
    if (row.absence.status === "approved") approvedHours += row.hours;
    else if (row.absence.status === "pending") pendingHours += row.hours;
  }
  return { rows, approvedHours, pendingHours };
}

export type ProjectBucket = "active" | "completed" | "internal";

/**
 * Hvilken liste et prosjekt hører til på ansattkortet. Interne prosjekter
 * først, så status: avsluttede og kansellerte er ferdige, kommende og aktive
 * er pågående. `projects.is_active` brukes ikke, den står fortsatt `true` på
 * de fleste avsluttede prosjektene.
 */
export function projectBucket(p: { is_internal: boolean; project_status: string | null }): ProjectBucket {
  if (p.is_internal) return "internal";
  if (p.project_status === "completed" || p.project_status === "cancelled") return "completed";
  return "active";
}

const PHASE_LABELS: Record<string, string> = {
  preprod: "Preproduksjon",
  production: "Produksjon",
  postprod: "Postproduksjon",
  faktura: "Faktura",
};

/** Status for et prosjekt i lista: fase for aktive, ellers status. */
export function projectStageLabel(p: { project_status: string | null; project_phase: string | null }): string {
  if (p.project_status === "completed") return "Avsluttet";
  if (p.project_status === "cancelled") return "Kansellert";
  if (p.project_status === "kommende") return "Kommende";
  return (p.project_phase && PHASE_LABELS[p.project_phase]) ?? "Aktiv";
}
